import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { CreditCard, AlertTriangle, Gift, ArrowRight, RotateCcw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const StripeButton = ({ selectedSize, selectedColor, boyshortsSize, boyshortsColor, quantity, productData, onError }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  const isBundle = productData?.id === 'bundle';

  const handleStripeCheckout = async () => {
    if (!selectedSize) {
      document.getElementById('bundle-size-selection')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById('size-selection')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // Bundle requires a boyshorts size too
    if (isBundle && !boyshortsSize) {
      document.querySelector('[data-testid="boyshorts-size-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsProcessing(true);
    setHasFailed(false);

    try {
      // Meta Pixel tracking
      if (window.fbq) {
        window.fbq('track', 'AddToCart', {
          content_name: productData.name,
          content_ids: [selectedColor + '-' + selectedSize],
          content_type: 'product',
          value: productData.price,
          currency: 'USD'
        });
        window.fbq('track', 'InitiateCheckout', {
          content_name: productData.name,
          content_ids: [selectedColor + '-' + selectedSize],
          content_type: 'product',
          value: productData.price,
          currency: 'USD'
        });
      }

      const response = await axios.post(`${API}/checkout/session`, {
        product_id: productData.id,
        size: selectedSize,
        color: selectedColor,
        boyshorts_size: boyshortsSize || null,
        boyshorts_color: boyshortsColor || null,
        quantity: quantity,
        origin_url: window.location.origin
      });

      const { url } = response.data;

      if (!url) {
        throw new Error('No checkout URL received from Stripe');
      }

      // Redirect to Stripe Checkout hosted page
      window.location.href = url;
    } catch (error) {
      setIsProcessing(false);
      setHasFailed(true);
      if (onError) {
        onError(error);
      }
    }
  };

  return (
    <div className="space-y-3">
      <Button
        onClick={handleStripeCheckout}
        disabled={isProcessing || !selectedSize}
        data-testid="stripe-checkout-button"
        className="w-full bg-gray-900 hover:bg-black text-white text-lg py-7 rounded-xl shadow-2xl transform hover:scale-105 transition-all duration-300 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isProcessing ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Redirecting to Checkout...
          </span>
        ) : (
          <span className="flex items-center justify-center">
            <CreditCard className="w-6 h-6 mr-2" />
            Pay with Card
          </span>
        )}
      </Button>

      {hasFailed && (
        <div
          data-testid="stripe-failure-recovery"
          className="rounded-xl border-2 border-rose-200 bg-rose-50 p-4"
        >
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-[#7d1f3d] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[#7d1f3d] font-bold text-sm">Card checkout didn&apos;t go through.</p>
              <p className="text-gray-700 text-xs mt-0.5">
                Try again, or pick one of the options below — your details aren&apos;t lost.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Button
              onClick={handleStripeCheckout}
              data-testid="stripe-retry-button"
              variant="outline"
              className="w-full border-[#7d1f3d] text-[#7d1f3d] hover:bg-[#7d1f3d] hover:text-white font-semibold"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Try Card Checkout Again
            </Button>

            {!isBundle && (
              <Link
                to="/"
                data-testid="stripe-upgrade-to-bundle"
                className="block group rounded-xl border-2 border-[#d4af37] bg-gradient-to-br from-[#7d1f3d] via-[#6d1a36] to-[#5d1729] text-white p-4 shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#d4af37] flex items-center justify-center">
                    <Gift className="w-5 h-5 text-[#5d1729]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#d4af37] text-[10px] font-bold uppercase tracking-wider mb-0.5">
                      One-click upgrade
                    </p>
                    <p className="text-white font-semibold text-sm leading-tight">
                      Get the Snatched Duo Bundle — both fajas + FREE boyshorts for $89.99
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#d4af37] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            )}

            <p className="text-center text-xs text-gray-600">
              Or scroll down to <strong>pay with PayPal</strong> instead.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
